import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Copy, Check, CreditCard, Sparkles, Loader2, Landmark, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import MotionReveal from "@/components/MotionReveal";
import api from "@/lib/api";
import { useLang } from "@/contexts/LangContext";

/** Bank & Revolut details are the couple's preferred (fee-free) methods. */
const REVOLUT = {
  handle: "@eliaslj1q",
  link: "https://revolut.me/eliaslj1q",
  holder: "Elias Theodorou",
};

const ACCOUNTS = [
  {
    id: "eur",
    currency: "EUR",
    label: "Euro (SEPA)",
    holder: "Elias Theodorou",
    rows: [
      { k: "IBAN", v: "LT52 3250 0533 4619 4599", copy: "LT523250053346194599" },
    ],
  },
  {
    id: "pln",
    currency: "PLN",
    label: "Polish złoty",
    holder: "Elias Theodorou",
    rows: [
      { k: "Account", v: "35 1020 1026 3741 0000 0000 20028", copy: "35102010263741000000020028" },
      { k: "Bank code", v: "10201026", copy: "10201026" },
      { k: "IBAN", v: "PL35 1020 1026 3741 0000 0000 20028", copy: "PL35102010263741000000020028" },
    ],
  },
  {
    id: "gbp",
    currency: "GBP",
    label: "British pound",
    holder: "Elias Theodorou",
    rows: [
      { k: "Account", v: "77581410", copy: "77581410" },
      { k: "Sort code", v: "23-14-70", copy: "231470" },
      { k: "IBAN", v: "GB47 TRWI 2314 7077 5814 10", copy: "GB47TRWI23147077581410" },
    ],
  },
  {
    id: "usd",
    currency: "USD",
    label: "US dollar",
    holder: "Elias Theodorou",
    rows: [
      { k: "Routing", v: "084009519", copy: "084009519" },
      { k: "Account", v: "199094798212251", copy: "199094798212251" },
    ],
  },
  {
    id: "cad",
    currency: "CAD",
    label: "Canadian dollar",
    holder: "Elias Theodorou",
    rows: [
      { k: "Account", v: "179830273", copy: "179830273" },
      { k: "Institution", v: "705", copy: "705" },
      { k: "Branch (Transit)", v: "00001", copy: "00001" },
    ],
  },
];

const CopyField = ({ label, value, copy, testid }) => {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(copy || value);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy — please copy manually.");
    }
  };
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-border/50 last:border-0">
      <div className="min-w-0">
        <p className="editorial-kicker text-foreground/50">{label}</p>
        <p className="mt-1 font-mono text-sm sm:text-base tracking-tight break-all text-foreground">{value}</p>
      </div>
      <button
        type="button"
        onClick={doCopy}
        data-testid={testid}
        className="shrink-0 mt-1 h-8 w-8 inline-flex items-center justify-center rounded-md border border-border text-foreground/60 hover:text-foreground hover:border-foreground/40 transition-colors"
        aria-label={`Copy ${label}`}
      >
        {copied ? <Check className="w-4 h-4 text-accent" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
};

const Gifts = () => {
  const { t, lang } = useLang();
  const [searchParams] = useSearchParams();
  const [summary, setSummary] = useState(null);

  // Stripe fallback state
  const [amount, setAmount] = useState("");
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (searchParams.get("cancelled")) toast.message(t("gifts.cancelled") || "Payment cancelled. No charge was made.");
    api.get("/gifts/public").then((r) => setSummary(r.data)).catch(() => {});
  }, [searchParams, t]);

  const submitCard = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt < 5) {
      toast.error(t("gifts.errAmount") || "Please enter an amount of at least €5.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        custom_amount: amt,
        origin_url: `${window.location.origin}/${lang}`,
        from_name: name,
        message: msg,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("gifts.errStart") || "Payment could not start.");
      setBusy(false);
    }
  };

  return (
    <div data-testid="gifts-page">
      {/* ─── Hero ─────────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-[1180px] px-5 sm:px-8 pt-24 sm:pt-32">
        <MotionReveal>
          <p className="editorial-kicker">{t("gifts.kicker") || "A gift for our day"}</p>
        </MotionReveal>
        <MotionReveal delay={0.05}>
          <h1 className="editorial-h1 mt-6 text-4xl sm:text-6xl lg:text-7xl max-w-[18ch]">
            {t("gifts.title") || "Help us build a home."}
          </h1>
        </MotionReveal>
        <MotionReveal delay={0.1}>
          <p className="mt-8 text-foreground/75 max-w-[62ch] leading-relaxed text-lg font-serif-display italic">
            {t("gifts.intro") ||
              "Your presence is the true gift. If you'd like to contribute towards our honeymoon, the details below are the ones we love most — bank transfer and Revolut mean every cent reaches us, without card fees."}
          </p>
        </MotionReveal>
        {summary && summary.gift_count > 0 && (
          <MotionReveal delay={0.15}>
            <div className="mt-8 surface-card p-5 inline-flex items-center gap-3" data-testid="gifts-progress">
              <Sparkles className="w-5 h-5 text-accent" />
              <span className="font-serif-display italic text-xl">
                {summary.gift_count}{" "}
                {summary.gift_count === 1
                  ? t("gifts.progressOne") || "friend has contributed so far. Thank you."
                  : t("gifts.progressMany") || "friends have contributed so far. Thank you."}
              </span>
            </div>
          </MotionReveal>
        )}
      </section>

      {/* ─── Preferred: Bank & Revolut ─────────────────────────────────── */}
      <section className="mx-auto max-w-[1180px] px-5 sm:px-8 mt-16 sm:mt-24" data-testid="gifts-preferred-section">
        <MotionReveal>
          <div className="flex items-baseline justify-between gap-6 flex-wrap">
            <div>
              <p className="editorial-kicker text-accent">{t("gifts.preferredKicker") || "Our preferred way"}</p>
              <h2 className="editorial-h2 mt-3 text-3xl sm:text-4xl">
                {t("gifts.preferredTitle") || "Bank transfer or Revolut."}
              </h2>
            </div>
            <p className="text-foreground/60 max-w-[38ch] text-sm leading-relaxed">
              {t("gifts.preferredNote") ||
                "No card fees, no middlemen. Any currency below works — pick whichever is cheapest for your bank."}
            </p>
          </div>
        </MotionReveal>

        {/* Revolut spotlight */}
        <MotionReveal delay={0.1}>
          <div
            className="mt-10 surface-card p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6"
            data-testid="gifts-revolut-card"
          >
            <div className="flex items-center gap-5 min-w-0">
              <div className="w-12 h-12 rounded-full bg-foreground/5 flex items-center justify-center shrink-0">
                <Smartphone className="w-5 h-5 text-foreground/70" />
              </div>
              <div className="min-w-0">
                <p className="editorial-kicker text-foreground/55">Revolut</p>
                <a
                  href={REVOLUT.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="gifts-revolut-link"
                  className="mt-2 block font-serif-display text-2xl sm:text-3xl lg:text-4xl tracking-tight break-all text-foreground hover:text-accent transition-colors"
                >
                  revolut.me/eliaslj1q
                </a>
                <p className="mt-1 text-foreground/60 text-sm">{REVOLUT.holder}</p>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <a
                href={REVOLUT.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="gifts-revolut-open"
                className="h-11 px-5 inline-flex items-center gap-2 rounded-lg bg-foreground text-background hover:bg-foreground/90 text-[11px] tracking-[0.22em] uppercase transition-colors"
              >
                <Smartphone className="w-4 h-4" />
                {t("gifts.openRevolut") || "Open"}
              </a>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(REVOLUT.link);
                    toast.success(t("gifts.linkCopied") || "Revolut link copied");
                  } catch {
                    toast.error("Could not copy — please copy manually.");
                  }
                }}
                data-testid="gifts-revolut-copy"
                className="h-11 w-11 inline-flex items-center justify-center rounded-lg border border-border hover:border-foreground/40 text-foreground/60 hover:text-foreground transition-colors"
                aria-label={t("gifts.copyHandle") || "Copy link"}
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
        </MotionReveal>

        {/* Bank accounts grid */}
        <div className="mt-8 grid md:grid-cols-2 gap-5" data-testid="gifts-bank-grid">
          {ACCOUNTS.map((acc, i) => (
            <MotionReveal delay={0.15 + i * 0.05} key={acc.id}>
              <div className="surface-card p-6 h-full" data-testid={`gifts-bank-${acc.id}`}>
                <div className="flex items-center gap-3">
                  <Landmark className="w-4 h-4 text-accent" />
                  <p className="editorial-kicker">{acc.currency} · {acc.label}</p>
                </div>
                <p className="mt-3 font-serif-display italic text-lg">{acc.holder}</p>
                <div className="mt-4">
                  {acc.rows.map((r) => (
                    <CopyField
                      key={r.k}
                      label={r.k}
                      value={r.v}
                      copy={r.copy}
                      testid={`gifts-copy-${acc.id}-${r.k.toLowerCase().replace(/\s+/g, "-")}`}
                    />
                  ))}
                </div>
              </div>
            </MotionReveal>
          ))}
        </div>
      </section>

      {/* ─── Stripe fallback (card) ────────────────────────────────────── */}
      <section className="mx-auto max-w-[920px] px-5 sm:px-8 mt-20 sm:mt-28 pb-24" data-testid="gifts-card-section">
        <MotionReveal>
          <p className="editorial-kicker">{t("gifts.cardKicker") || "Or, if it's easier"}</p>
          <h2 className="editorial-h2 mt-3 text-3xl sm:text-4xl">
            {t("gifts.cardTitle") || "Pay by card."}
          </h2>
          <p className="mt-4 text-foreground/65 max-w-[58ch] leading-relaxed text-[15px]">
            {t("gifts.cardNote") ||
              "A secure card payment via Stripe. There's a small processing fee — if you can, the bank details above are gentler on us both."}
          </p>
        </MotionReveal>

        <form onSubmit={submitCard} className="mt-10 space-y-8">
          <div className="space-y-2">
            <Label className="editorial-kicker" htmlFor="gift-amount">
              {t("gifts.amountLabel") || "Amount"}
            </Label>
            <div className="flex items-baseline gap-3">
              <span className="font-serif-display text-4xl text-foreground/80">€</span>
              <Input
                id="gift-amount"
                type="number"
                inputMode="decimal"
                min="5"
                max="5000"
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                data-testid="gift-amount-input"
                className="h-14 w-full max-w-[220px] border-0 border-b border-border rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-accent tabular-nums font-serif-display text-4xl px-0"
              />
            </div>
            <p className="text-xs text-foreground/50 mt-2">
              {t("gifts.amountHint") || "Any amount from €5 to €5,000."}
            </p>
          </div>

          <div className="space-y-2">
            <Label className="editorial-kicker" htmlFor="gift-name">
              {t("gifts.nameLabel") || "Your name (optional)"}
            </Label>
            <Input
              id="gift-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="gift-name-input"
              maxLength={80}
              className="h-12 border-0 border-b border-border rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-accent"
            />
          </div>

          <div className="space-y-2">
            <Label className="editorial-kicker" htmlFor="gift-msg">
              {t("gifts.messageLabel") || "A private note to us (optional)"}
            </Label>
            <Textarea
              id="gift-msg"
              rows={3}
              value={msg}
              onChange={(e) => setMsg(e.target.value.slice(0, 400))}
              maxLength={400}
              data-testid="gift-message-input"
              className="border-0 border-b border-border rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-accent resize-none"
            />
            <p className="text-xs text-foreground/50 mt-2">
              {t("gifts.messagePrivate") || "Only we will see this — it will never appear publicly."}
            </p>
          </div>

          <div className="pt-2">
            <Button
              type="submit"
              disabled={busy}
              data-testid="gift-submit"
              className="h-12 px-8 rounded-xl bg-foreground text-background hover:bg-foreground/90 tracking-[0.22em] uppercase text-[12px] gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              {busy
                ? t("gifts.opening") || "Opening Stripe…"
                : t("gifts.payCta") || "Continue to Stripe"}
            </Button>
            <p className="mt-3 editorial-kicker text-foreground/45">
              {t("gifts.secure") || "Secure payment · Stripe · 3D-Secure supported"}
            </p>
          </div>
        </form>
      </section>
    </div>
  );
};

export default Gifts;

/** Thank-you page shown after Stripe redirects back. */
export const GiftsThanks = () => {
  const { t } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("pending");
  const [tries, setTries] = useState(0);
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setStatus(data.payment_status);
        if (data.payment_status === "paid") return;
        if (tries > 20) return;
        setTimeout(() => setTries((n) => n + 1), 2000);
      } catch {
        if (!cancelled) setTimeout(() => setTries((n) => n + 1), 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, tries]);

  const paid = status === "paid";
  return (
    <div className="mx-auto max-w-[720px] px-5 sm:px-8 pt-32 pb-24 text-center" data-testid="gifts-thanks-page">
      {paid ? (
        <Sparkles className="w-12 h-12 text-accent mx-auto" />
      ) : (
        <Loader2 className="w-10 h-10 text-foreground/40 mx-auto animate-spin" />
      )}
      <h1 className="editorial-h1 mt-6 text-4xl sm:text-6xl">
        {paid ? t("gifts.thanksTitle") || "Thank you." : t("gifts.thanksConfirming") || "Confirming…"}
      </h1>
      <p className="mt-6 font-serif-display italic text-xl text-foreground/75 max-w-[42ch] mx-auto">
        {paid
          ? t("gifts.thanksBody") ||
            "Your gift lands with us in Cyprus with a hug from us both. We can't wait to see you in May."
          : t("gifts.thanksWait") || "Just a moment while Stripe confirms the payment."}
      </p>
      {paid && (
        <Button
          onClick={() => navigate("/")}
          className="mt-10 h-12 px-8 rounded-xl bg-foreground text-background hover:bg-foreground/90 tracking-[0.22em] uppercase text-[12px]"
          data-testid="gifts-thanks-home"
        >
          {t("gifts.thanksHome") || "Back to the site"}
        </Button>
      )}
    </div>
  );
};
